import { DEFAULT_LOCALE, resources, SUPPORTED_LOCALES, toLocale, type Locale } from '@zuruflex/i18n';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

const STORAGE_KEY = 'zuruflex.locale';

/**
 * Catalogues come from `@zuruflex/i18n`, the same object the API loads, so the
 * two can never disagree about what a phrase says.
 *
 * Detection order: a saved choice first, then the browser's own languages.
 * The choice is persisted so a returning visitor keeps their language.
 */
const ready = i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: resources as never,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    // Regional tags such as fr-CA collapse onto the base language we ship.
    load: 'languageOnly',
    defaultNS: 'partner',
    ns: ['partner', 'common'],
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  });

/**
 * Keeps <html lang> in step, which matters for screen readers and Google.
 *
 * `languageChanged` only fires on a change and `initialized` can fire
 * before a listener attaches, so the first value is applied off the init
 * promise — otherwise the first paint claims English whatever was detected.
 */
function syncDocumentLanguage(language: string): void {
  document.documentElement.lang = toLocale(language);
}

i18next.on('languageChanged', syncDocumentLanguage);
void ready.then(() => syncDocumentLanguage(i18next.language));

export function currentLocale(): Locale {
  return toLocale(i18next.language);
}

export { i18next };
